import React, { forwardRef, useEffect, useState } from 'react'
import type { ReactElement, MouseEventHandler } from 'react'
import DatePicker from 'react-datepicker'
import type { Nullable } from '../../types/nullable'
import DateHelpers from './helpers/DateHelpers'
import DeliveryDateShortCut from './DeliveryDateShortCut'
import calendar from './assets/calendar.svg'
import fitnessCalendar from './assets/fitness-calendar.svg'
import 'react-datepicker/dist/react-datepicker.css'
import { BasketModel, ShippingClassModel } from './types/models'
import type { KioskCollectTodayState } from './types/KioskCollectToday'
import KioskCollectTodayDateOption from './KioskCollectTodayDateOption'

type ChooseDeliveryDateProps = {
  basket: BasketModel;
  deliveryDates: Date[];
  deliveryDate: Nullable<Date>;
  shippingClass: ShippingClassModel;
  onDeliveryDateChanged: (date: Date, kioskCollectToday?: boolean) => void;
  toggleEarlyDelivery: () => void;
  canToggleEarlyDelivery: boolean;
  kioskCollectToday?: KioskCollectTodayState | null;
}

type CustomInputProps = {
  value?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Represents the DeliveryDate options for basket.
 * User can select a delivery date using the shortcuts provided or
 * by clicking through a calendar.
 */
const ChooseDeliveryDate = (
  {
    basket, deliveryDates, deliveryDate, shippingClass,
    onDeliveryDateChanged, toggleEarlyDelivery, canToggleEarlyDelivery,
    kioskCollectToday
  }: ChooseDeliveryDateProps
): ReactElement => {
  /**
   * DatePickerButton - used for setting custom input on ReactDatePicker
   */
  const DatePickerButton = forwardRef(
    ({ value, onClick }: CustomInputProps, ref: React.Ref<HTMLButtonElement>) => (
      <button className="delivery-date-shortcut delivery-calendar-button" onClick={onClick} title={(value || 'Select a date') as string} ref={ref}>
        {value}
      </button>
    )
  )

  /**
   * Please ESLint and TS by setting a display name.
   */
  DatePickerButton.displayName = 'DatePickerButton'

  /**
   * The first 3 dates possible for delivery
   */
  const [deliveryDateShortCuts, setDeliveryDateShortCuts] = useState<Date[]>([])

  /**
   * Returns the first 3 dates of given delivery dates
   */
  function defaultDeliveryDateShortCuts (): Date[] {
    return deliveryDates.slice(0, 3)
  }

  function appendFutureDateToShortcuts (date: Date): void {
    setDeliveryDateShortCuts((prev): Date[] => {
      if (DateHelpers.isInArray(date, defaultDeliveryDateShortCuts())) {
        return defaultDeliveryDateShortCuts()
      }

      if (DateHelpers.isInArray(date, prev)) {
        return prev
      }

      // Last el stays - do not remove
      // Tradecounter - different delivery days(?)
      return defaultDeliveryDateShortCuts().concat([date])
    })
  }

  /**
   * Adds another delivery date short cut to show "selected"
   * state of calendar. Will only add if not in the first 3 shortcuts
   * Runs a network request to verify afterward
   * @param date User selected date
   */
  function onSelectDeliveryDate (date: Date): void {
    appendFutureDateToShortcuts(date)

    onDeliveryDateChanged(date)
  }

  function onSelectCollectToday (): void {
    if (kioskCollectToday) {
      onDeliveryDateChanged(kioskCollectToday.date, true)
    }
  }

  /**
   * Set initial delivery date shortcuts to first 3.
   */
  useEffect(() => {
    setDeliveryDateShortCuts(defaultDeliveryDateShortCuts())

    if (deliveryDate !== null && !kioskCollectToday?.selected) {
      onSelectDeliveryDate(deliveryDate)
    }
  }, [])

  useEffect(() => {
    setDeliveryDateShortCuts(defaultDeliveryDateShortCuts())

    if (deliveryDate !== null && !kioskCollectToday?.selected) {
      appendFutureDateToShortcuts(deliveryDate)
    }
  }, [
    deliveryDates,
    kioskCollectToday?.available,
    kioskCollectToday?.selected
  ])

  const deliveryDateSelection = (
    <div className="delivery-date-selection-container">
      <div className="delivery-date-shortcuts">
        {deliveryDateShortCuts.map((shortCutDeliveryDate: Date, index: number):
        ReactElement => (
          <DeliveryDateShortCut
            thisDeliveryDate={shortCutDeliveryDate}
            deliveryDate={deliveryDate}
            onDeliveryDateChanged={onSelectDeliveryDate}
            key={index}
          />
        ))}

        <DatePicker
          onChange={(date: Date): void => onSelectDeliveryDate(date)}
          selected={deliveryDate}
          includeDates={deliveryDates}
          customInput={<DatePickerButton />}
        />
      </div>
    </div>
  )

  const deliveryDateOptions = kioskCollectToday
    ? (
        <div className="delivery-date-options">
          <KioskCollectTodayDateOption
            state={kioskCollectToday}
            onSelect={onSelectCollectToday}
          />
          {deliveryDateSelection}
        </div>
      )
    : deliveryDateSelection

  return (
    <>
      <div className="choose-delivery-date-header">
        <h2 className="heading-with-image">
          <img
            alt=""
            className="icon tms-calendar-icon"
            aria-hidden="true"
            loading="lazy"
            src={calendar} width="15" height="16"
          />
          <img
            alt=""
            className="icon fitness-calendar-icon"
            aria-hidden="true"
            loading="lazy"
            src={fitnessCalendar} width="15" height="16"
          />
            Choose { shippingClass.is_collection ? 'Collection' : 'Delivery'} Date
        </h2>
        {deliveryDateOptions}
        <p className={`delivery-date-selection basket-warning ${deliveryDate == null ? '' : 'hidden'}`}>
          Please choose a { shippingClass.is_collection ? 'collection' : 'delivery'} date.
        </p>

        {
          canToggleEarlyDelivery && <label className="early-delivery-checkbox" htmlFor="early_delivery">
            I am happy to receive items sooner than the selected delivery date, if possible
            <input type="checkbox" name="early_delivery" checked={basket.early_delivery}
            onChange={
              (): void => { toggleEarlyDelivery() }
            }/>
          </label>
        }
      </div>
    </>
  )
}

export default ChooseDeliveryDate
